import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, DeniedState } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Trash2, ShieldCheck, KeyRound, Copy, Database, Download, PlayCircle, CloudUpload, CheckCircle2, AlertTriangle, RotateCcw, RefreshCw, CreditCard, ExternalLink } from "lucide-react";

const AVAILABLE_FRAMEWORKS = [
  { code: "NIST-CSF", name: "NIST CSF 2.0" },
  { code: "ISO27001", name: "ISO/IEC 27001:2022" },
  { code: "ISO42001", name: "ISO/IEC 42001:2023 (AI)" },
  { code: "CIS-v8", name: "CIS Controls v8" },
  { code: "NZISM", name: "NZISM" },
  { code: "E8", name: "Essential Eight (ACSC)" },
  { code: "SCM0020", name: "NSW SCM0020" },
];

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ALLOWED_MIME = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];

export default function Settings() {
  const { user, tenant, refresh } = useAuth();
  const fileRef = useRef(null);
  const [name, setName] = useState(tenant?.name || "");
  const [color, setColor] = useState(tenant?.primary_color || "#0E7490");
  const [logoUrl, setLogoUrl] = useState(tenant?.logo_url || "");
  const [logoData, setLogoData] = useState(tenant?.logo_data || "");
  const [active, setActive] = useState(tenant?.active_frameworks || []);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setName(tenant?.name || "");
    setColor(tenant?.primary_color || "#0E7490");
    setLogoUrl(tenant?.logo_url || "");
    setLogoData(tenant?.logo_data || "");
    setActive(tenant?.active_frameworks || []);
  }, [tenant]);

  if (user?.role !== "Admin") return <div className="max-w-7xl mx-auto px-6 py-10"><DeniedState requiredRoles={["Admin"]} /></div>;

  const toggle = (code) => setActive((a) => (a.includes(code) ? a.filter((c) => c !== code) : [...a, code]));

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_MIME.includes(f.type)) { toast.error("Unsupported logo type. Use PNG, JPEG, WebP, or SVG."); return; }
    if (f.size > MAX_LOGO_BYTES) { toast.error(`Logo too large (${(f.size / 1024 / 1024).toFixed(2)} MB). Max 2 MB.`); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/tenant/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoData(data.logo_data);
      toast.success(`Logo uploaded (${(data.size / 1024).toFixed(1)} KB)`);
      await refresh();
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    setUploading(true);
    try { await api.delete("/tenant/logo"); setLogoData(""); toast.success("Logo removed"); await refresh(); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/tenant", { name, primary_color: color, logo_url: logoUrl || null, active_frameworks: active });
      toast.success("Tenant updated");
      await refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader kicker="Settings" title="Tenant settings" description="Brand your tenant and pick which standards you're following." />

      {/* Branding */}
      <Card className="p-6 space-y-5 mb-5" data-testid="settings-branding-card">
        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">Branding</div>
        <div>
          <Label>Tenant name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="tenant-name-input" />
        </div>

        <div>
          <Label>Logo (PNG, JPEG, WebP or SVG — max 2 MB)</Label>
          <div className="flex items-center gap-4 mt-1">
            <div className="h-16 w-16 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
              {logoData ? <img src={logoData} alt="logo" className="max-h-full max-w-full" data-testid="tenant-logo-preview" /> : <span className="text-[10px] text-slate-400 font-mono">no logo</span>}
            </div>
            <input ref={fileRef} type="file" accept={ALLOWED_MIME.join(",")} onChange={onLogoFile} className="hidden" data-testid="tenant-logo-file-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="tenant-logo-upload-btn">
              <Upload className="h-3.5 w-3.5 mr-1.5" /> {uploading ? "Uploading…" : "Upload logo"}
            </Button>
            {logoData && (
              <Button type="button" variant="ghost" onClick={removeLogo} disabled={uploading} className="text-rose-600 hover:text-rose-700" data-testid="tenant-logo-remove-btn">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
              </Button>
            )}
          </div>
        </div>

        <div>
          <Label>Logo URL (fallback — used if no upload)</Label>
          <Input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://…" data-testid="tenant-logo-input" />
        </div>

        <div>
          <Label>Primary accent color</Label>
          <div className="flex gap-3 items-center">
            <Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-16 h-10 p-1" data-testid="tenant-color-input" />
            <Input value={color} onChange={(e) => setColor(e.target.value)} className="font-mono w-40" />
            <div className="ml-auto flex items-center gap-2">
              <div className="h-8 w-8 rounded-md border" style={{ background: color }} />
              <span className="font-mono text-xs">preview</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Frameworks */}
      <Card className="p-6 space-y-4 mb-5" data-testid="settings-frameworks-card">
        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">Standards this company is following</div>
        <p className="text-sm text-slate-600">Select the frameworks your organisation is aligned to. Only active frameworks appear in the mapping UI and executive reports.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {AVAILABLE_FRAMEWORKS.map((fw) => (
            <label key={fw.code} className="flex items-center gap-2 p-2 rounded border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid={`fw-toggle-${fw.code}`}>
              <Checkbox checked={active.includes(fw.code)} onCheckedChange={() => toggle(fw.code)} />
              <div>
                <div className="text-sm font-semibold text-slate-900">{fw.name}</div>
                <div className="text-[10px] font-mono text-slate-500">{fw.code}</div>
              </div>
            </label>
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="tenant-save">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <TwoFactorCard />
      <AdvisorEditor />
      <BillingCard />
      <BackupCard />
    </div>
  );
}

function BillingCard() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/payments/stripe-status").then(({ data }) => setStatus(data)).catch(() => setStatus({ mode: "test", configured: false, claim_url: "https://dashboard.stripe.com/register", dashboard_url: "https://dashboard.stripe.com" }));
  }, []);
  if (!status) return null;
  const isTest = status.mode !== "live";
  return (
    <Card className="p-6 space-y-4 mt-5" data-testid="settings-billing-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${isTest ? "bg-amber-50 text-amber-600 border border-amber-200" : "bg-emerald-50 text-emerald-600 border border-emerald-200"}`}>
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Billing & Stripe</div>
            <div className="font-display font-semibold text-slate-900 text-base mt-0.5">
              {isTest ? "You're in Stripe test mode" : "Stripe live mode is active"}
            </div>
          </div>
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full border ${isTest ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
          data-testid="stripe-mode-badge"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${isTest ? "bg-amber-500" : "bg-emerald-500"}`} />
          {isTest ? "Test" : "Live"}
        </span>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed">
        {isTest
          ? "Checkout works end-to-end, but no money moves. Claim your Stripe account to unlock live payouts for real customers — Stripe handles the KYC, tax collection, and payouts to your bank."
          : "You're set up for live payouts. Any purchase on the public pricing page will charge the customer's card and settle to your connected Stripe account."}
      </p>

      {isTest && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-[13px] text-amber-900 leading-relaxed">
          <div className="font-semibold mb-1 flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Three things you'll need to claim it
          </div>
          <ol className="list-decimal pl-5 space-y-0.5 marker:text-amber-500">
            <li>A business email and legal entity name</li>
            <li>Bank account details for payouts</li>
            <li>5–10 minutes to complete Stripe's verification</li>
          </ol>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <a
          href={status.claim_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex"
          data-testid="stripe-claim-link"
        >
          <Button className="bg-[#0A1329] hover:bg-[#162347] text-white">
            {isTest ? "Claim my Stripe account" : "Open Stripe dashboard"}
            <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </a>
        {!isTest && (
          <a
            href={status.dashboard_url}
            target="_blank"
            rel="noreferrer"
            data-testid="stripe-dashboard-link"
          >
            <Button variant="outline">
              View payouts
              <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </a>
        )}
      </div>
    </Card>
  );
}

function AdvisorEditor() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/tenant/advisor").then(({ data }) => setForm(data || {})).catch(() => setForm({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!form) return null;
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name?.trim() || "",
        title: form.title?.trim() || null,
        email: form.email?.trim() || null,
        calendar_url: form.calendar_url?.trim() || null,
        bio: form.bio?.trim() || null,
        focus_areas: (form.focus_areas_str || (form.focus_areas || []).join(", "))
          .split(",").map((s) => s.trim()).filter(Boolean),
        next_session_at: form.next_session_at ? new Date(form.next_session_at).toISOString() : null,
      };
      if (!payload.name) return toast.error("Advisor name required");
      await api.patch("/tenant/advisor", payload);
      toast.success("Advisor updated");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const dtValue = form.next_session_at ? String(form.next_session_at).slice(0, 16) : "";
  return (
    <Card className="p-6 space-y-4 mt-5" data-testid="settings-advisor-card">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Assigned advisor</div>
        <p className="text-sm text-slate-600 mt-1">This appears in the top-right advisor widget for every user in this tenant.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Name *</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="advisor-editor-name" /></div>
        <div><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="advisor-editor-title" /></div>
        <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="advisor-editor-email" /></div>
        <div><Label>Booking / calendar URL</Label><Input value={form.calendar_url || ""} onChange={(e) => setForm({ ...form, calendar_url: e.target.value })} placeholder="https://cal.com/…" data-testid="advisor-editor-cal" /></div>
        <div className="sm:col-span-2"><Label>Focus areas (comma separated)</Label>
          <Input value={form.focus_areas_str ?? (form.focus_areas || []).join(", ")} onChange={(e) => setForm({ ...form, focus_areas_str: e.target.value })} placeholder="ISO 27001, AI Governance, Board reporting" data-testid="advisor-editor-focus" /></div>
        <div className="sm:col-span-2"><Label>Bio</Label>
          <Input value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Short introduction shown in the widget popover" data-testid="advisor-editor-bio" /></div>
        <div className="sm:col-span-2"><Label>Next session</Label>
          <Input type="datetime-local" value={dtValue} onChange={(e) => setForm({ ...form, next_session_at: e.target.value })} data-testid="advisor-editor-session" /></div>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="advisor-editor-save">
          {busy ? "Saving…" : "Save advisor"}
        </Button>
      </div>
    </Card>
  );
}

function TwoFactorCard() {
  const [status, setStatus] = useState(null);
  const [showRegen, setShowRegen] = useState(false);
  const [code, setCode] = useState("");
  const [newCodes, setNewCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/auth/2fa/status"); setStatus(data); }
      catch (err) { console.warn("Settings: failed to fetch 2FA status", err); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const regenerate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/regenerate-recovery", { code });
      setNewCodes(data.recovery_codes);
      const s = await api.get("/auth/2fa/status");
      setStatus(s.data);
      setCode("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Card className="p-6 space-y-4 mt-5" data-testid="settings-2fa-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">Two-factor authentication</div>
            <div className="flex items-center gap-2 mt-2">
              <ShieldCheck className={`h-5 w-5 ${status?.enabled ? "text-emerald-600" : "text-slate-400"}`} />
              <span className="text-sm font-semibold text-slate-900" data-testid="2fa-status">
                {status?.enabled ? "Enabled — required on every sign-in" : "Not enabled"}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-1 font-mono">
              {status ? `${status.recovery_codes_remaining} recovery code${status.recovery_codes_remaining === 1 ? "" : "s"} remaining` : "…"}
            </div>
          </div>
          {status?.enabled && (
            <Button variant="outline" size="sm" onClick={() => setShowRegen(true)} data-testid="regen-recovery-btn">
              <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Regenerate recovery codes
            </Button>
          )}
        </div>
      </Card>

      {showRegen && !newCodes && (
        <Dialog open={true} onOpenChange={(o) => !o && setShowRegen(false)}>
          <DialogContent className="max-w-md" data-testid="regen-dialog">
            <DialogHeader><DialogTitle className="font-display">Regenerate recovery codes</DialogTitle></DialogHeader>
            <form onSubmit={regenerate} className="space-y-3">
              <div className="text-sm text-slate-600">Enter the 6-digit code from your authenticator to invalidate your existing recovery codes and generate 10 fresh ones.</div>
              <div>
                <Label>Current 6-digit code</Label>
                <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required data-testid="regen-code" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setShowRegen(false)}>Cancel</Button>
                <Button type="submit" disabled={busy || code.length !== 6} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="regen-submit">Regenerate</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {newCodes && (
        <Dialog open={true} onOpenChange={() => { setNewCodes(null); setShowRegen(false); }}>
          <DialogContent className="max-w-md" data-testid="regen-result">
            <DialogHeader><DialogTitle className="font-display">Save your new recovery codes</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="text-sm text-slate-600">Store these somewhere safe. Each code works once and this is the only time they will be shown.</div>
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 grid grid-cols-2 gap-2 font-mono text-sm" data-testid="new-recovery-codes">
                {newCodes.map((c) => <div key={c} className="px-2 py-1 bg-white rounded border border-amber-200 text-center tracking-wider">{c}</div>)}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => { navigator.clipboard.writeText(newCodes.join("\n")); toast.success("Copied"); }}>
                  <Copy className="h-4 w-4 mr-1.5" /> Copy all
                </Button>
                <Button type="button" className="flex-1 bg-[#0A1329] hover:bg-[#162347] text-white" onClick={() => { setNewCodes(null); setShowRegen(false); toast.success("Codes rotated"); }}>Done</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function BackupCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [restoreFile, setRestoreFile] = useState(null); // filename opened in the restore dialog
  const [restorePreview, setRestorePreview] = useState(null);
  const [restorePhrase, setRestorePhrase] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotatePhrase, setRotatePhrase] = useState("");
  const [rotating, setRotating] = useState(false);
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try { const { data } = await api.get("/backup/status"); setState(data); }
    catch (err) { console.warn("BackupCard: fetch failed", err); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      await api.post("/backup/run-now");
      toast.success("Backup queued — refresh in a few seconds");
      setTimeout(load, 4000);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const download = async (filename) => {
    setDownloading(filename);
    try {
      const token = localStorage.getItem("sq_token");
      const r = await fetch(`${REACT_APP_BACKEND_URL}/api/backup/download/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (err) { toast.error(`Download failed: ${err.message}`); }
    finally { setDownloading(""); }
  };

  const openRestore = async (filename) => {
    setRestoreFile(filename); setRestorePreview(null); setRestorePhrase("");
    try {
      const { data } = await api.post("/backup/restore", { filename, mode: "dry-run" });
      setRestorePreview(data.preview);
    } catch (e) { toast.error(apiError(e)); setRestoreFile(null); }
  };

  const executeRestore = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post("/backup/restore", {
        filename: restoreFile, mode: "execute", confirmation: restorePhrase,
      });
      toast.success(`Restored ${data.stats.collections ? Object.keys(data.stats.collections).length : 0} collections`);
      setRestoreFile(null); setRestorePreview(null); setRestorePhrase("");
      setTimeout(load, 500);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRestoring(false); }
  };

  const executeRotate = async () => {
    setRotating(true);
    try {
      const { data } = await api.post("/backup/rotate-key", { confirmation: rotatePhrase });
      toast.success(`New key ${data.after.primary_fingerprint} · ${data.rewrap.local_rewrapped} file(s) rewrapped`);
      setRotateOpen(false); setRotatePhrase("");
      setTimeout(load, 500);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRotating(false); }
  };

  const fmtBytes = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <Card className="p-6 mt-5" data-testid="settings-backup-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold flex items-center gap-1.5">
            <Database className="h-3.5 w-3.5" /> Encrypted backups
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Every 12 hours the entire tenant database is Fernet-encrypted and saved to <span className="font-mono text-xs">{state?.backup_dir || "/app/backups"}</span>. Retention keeps the newest {state?.retain_count || 28} files (~14 days).
          </p>
          {state?.s3?.bucket ? (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest" data-testid="backup-s3-status">
              <CloudUpload className="h-3.5 w-3.5" /> S3 mirror · {state.s3.bucket}
            </div>
          ) : (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600 px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest">
              <CloudUpload className="h-3.5 w-3.5" /> S3 mirror · off
            </div>
          )}
        </div>
        <Button onClick={runNow} disabled={busy} className="bg-[#0A1329] hover:bg-[#162347] text-white shrink-0" data-testid="backup-run-now">
          <PlayCircle className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Queuing…" : "Backup now"}
        </Button>
      </div>

      {/* Keyring status + rotate action */}
      {state?.keyring && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 mb-4" data-testid="keyring-status">
          <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest font-mono text-slate-500 font-semibold">
            <KeyRound className="h-3 w-3" /> Backup key
          </span>
          <span className="font-mono text-xs text-slate-800" data-testid="keyring-fingerprint">{state.keyring.primary_fingerprint}</span>
          <span className="text-[10px] uppercase tracking-widest font-mono text-slate-400">· source {state.keyring.source}</span>
          {state.keyring.previous_count > 0 && (
            <span className="text-[10px] uppercase tracking-widest font-mono text-slate-400">· {state.keyring.previous_count} older</span>
          )}
          {state.keyring.rotated_at && (
            <span className="text-[10px] font-mono text-slate-400">last rotated {fmtDate(state.keyring.rotated_at)}</span>
          )}
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setRotateOpen(true)} data-testid="backup-rotate-key">
            <RefreshCw className="h-3 w-3 mr-1" /> Rotate key
          </Button>
        </div>
      )}

      {!state ? (
        <div className="text-sm text-slate-500 italic">Loading backup history…</div>
      ) : state.backups.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50/60 px-4 py-6 text-sm text-slate-500 text-center">
          No backups yet. Click "Backup now" to create the first one — or wait for the next 12-hour cron tick.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="w-full text-[12.5px]" data-testid="backup-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Docs</th>
                <th className="text-left px-3 py-2">Encrypted</th>
                <th className="text-left px-3 py-2">SHA-256</th>
                <th className="text-left px-3 py-2">S3</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.backups.map((b) => {
                const docCount = Object.values(b.collections || {}).reduce((a, c) => a + c, 0);
                const s3Ok = !!(b.s3 && b.s3.bucket);
                return (
                  <tr key={b.file} className="border-b border-slate-100 last:border-0" data-testid={`backup-row-${b.file}`}>
                    <td className="px-3 py-2 font-mono text-slate-800">{fmtDate(b.created_at)}</td>
                    <td className="px-3 py-2 font-mono tabular-nums text-slate-700">{docCount.toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono tabular-nums text-slate-700">{fmtBytes(b.encrypted_bytes)}</td>
                    <td className="px-3 py-2 font-mono text-[10.5px] text-slate-500" title={b.sha256}>{(b.sha256 || "").slice(0, 12)}…</td>
                    <td className="px-3 py-2">
                      {s3Ok ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> synced</span>
                      ) : b.s3_error ? (
                        <span className="inline-flex items-center gap-1 text-amber-700" title={b.s3_error}><AlertTriangle className="h-3 w-3" /> failed</span>
                      ) : (
                        <span className="text-slate-400">local only</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" disabled={downloading === b.file} onClick={() => download(b.file)} data-testid={`backup-download-${b.file}`}>
                          <Download className="h-3 w-3 mr-1" /> {downloading === b.file ? "…" : ".enc"}
                        </Button>
                        <Button size="sm" variant="outline" className="text-rose-700 hover:text-rose-800 hover:bg-rose-50 border-rose-200" onClick={() => openRestore(b.file)} data-testid={`backup-restore-${b.file}`}>
                          <RotateCcw className="h-3 w-3 mr-1" /> Restore
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
        Backups are encrypted with a dedicated Fernet keyring (see <span className="font-mono">backup_crypto.py</span>) — rotating the backup key does <em>not</em> touch <span className="font-mono">JWT_SECRET</span>, so active sessions and TOTP secrets survive. Weekly restore-drill emails go to every super-admin every Monday.
      </p>

      {/* Restore dialog — 2 stage: dry-run preview then typed confirmation. */}
      {restoreFile && (
        <Dialog open={true} onOpenChange={(o) => !o && setRestoreFile(null)}>
          <DialogContent className="max-w-2xl" data-testid="restore-dialog">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2 text-rose-800">
                <AlertTriangle className="h-4 w-4" /> Restore from backup — destructive
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-slate-700 space-y-3">
              <p>
                Restoring will <strong>drop every collection</strong> in the tenant database and replace it with the contents of{" "}
                <span className="font-mono text-[12px]">{restoreFile}</span>. Your current admin session is preserved so you are not locked out.
              </p>
              {!restorePreview ? (
                <div className="italic text-slate-500">Loading dry-run preview…</div>
              ) : (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 max-h-[240px] overflow-auto">
                  <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold mb-2">
                    Dry-run · {restorePreview.total_docs.toLocaleString()} docs across {Object.keys(restorePreview.collections).length} collections
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] font-mono">
                    {Object.entries(restorePreview.collections).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-slate-600">{k}</span>
                        <span className="tabular-nums text-slate-900">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="rp" className="text-[11px] uppercase tracking-widest font-mono text-slate-500">
                  Type <span className="text-rose-700">RESTORE {restoreFile.replace(".json.enc", "")}</span> to confirm
                </Label>
                <Input
                  id="rp" value={restorePhrase} onChange={(e) => setRestorePhrase(e.target.value)}
                  className="font-mono text-xs mt-1" placeholder={`RESTORE ${restoreFile.replace(".json.enc", "")}`}
                  data-testid="restore-phrase"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRestoreFile(null)}>Cancel</Button>
              <Button
                onClick={executeRestore}
                disabled={restoring || restorePhrase.trim() !== `RESTORE ${restoreFile.replace(".json.enc", "")}`}
                className="bg-rose-700 hover:bg-rose-800 text-white"
                data-testid="restore-confirm"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> {restoring ? "Restoring…" : "Restore now"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Rotate-key dialog */}
      {rotateOpen && (
        <Dialog open={true} onOpenChange={(o) => !o && setRotateOpen(false)}>
          <DialogContent className="max-w-lg" data-testid="rotate-key-dialog">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-cyan-700" /> Rotate backup encryption key
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-slate-700 space-y-3">
              <p>
                Generates a fresh Fernet key, then decrypts every backup and re-encrypts it with the new key.
                The old key is retained in the keyring so historical S3 files stay decryptable during the rewrap.
                <span className="font-semibold"> Active user sessions and TOTP secrets are unaffected.</span>
              </p>
              <div>
                <Label htmlFor="rot" className="text-[11px] uppercase tracking-widest font-mono text-slate-500">
                  Type <span className="text-slate-900">ROTATE BACKUP KEY</span> to confirm
                </Label>
                <Input
                  id="rot" value={rotatePhrase} onChange={(e) => setRotatePhrase(e.target.value)}
                  className="font-mono text-xs mt-1" placeholder="ROTATE BACKUP KEY"
                  data-testid="rotate-phrase"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRotateOpen(false)}>Cancel</Button>
              <Button
                onClick={executeRotate}
                disabled={rotating || rotatePhrase.trim() !== "ROTATE BACKUP KEY"}
                className="bg-[#0A1329] hover:bg-[#162347] text-white"
                data-testid="rotate-confirm"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> {rotating ? "Rotating…" : "Rotate now"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

